import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import "./index.css";

import Overlay from "../Overlay";
import { createApplication } from "../../api-requests/application";

const PHASES = {
    CONFIRM: "confirm",
    SENDING: "sending",
    SUCCESS: "success",
    ERROR: "error",
};

export default ({ show, job, profile, onClose, onApplied }) => {

    const [phase, setPhase] = useState(PHASES.CONFIRM);
    const [error, setError] = useState(null);
    const history = useHistory();

    const goToCv = () => {
        onClose();
        history.push("/profile/cv");
    };

    useEffect(() => {
        if (!show) {
            // Reset on close so the next apply starts fresh.
            setPhase(PHASES.CONFIRM);
            setError(null);
            return;
        }
        const onKey = (e) => {
            if (e.key === "Escape" && phase !== PHASES.SENDING) onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [show, phase, onClose]);

    if (!show || !job) return null;

    const handleSend = () => {
        if (phase === PHASES.SENDING) return;
        setPhase(PHASES.SENDING);
        setError(null);

        // Track elapsed so the sending state stays up for at least ~700ms —
        // even on instant resolves, the celebration registers properly.
        const startedAt = Date.now();
        createApplication(job, profile)
            .then(result => {
                const elapsed = Date.now() - startedAt;
                const remaining = Math.max(0, 700 - elapsed);
                setTimeout(() => {
                    if (result && result._id) {
                        setPhase(PHASES.SUCCESS);
                        if (onApplied) onApplied(result);
                    } else {
                        setPhase(PHASES.ERROR);
                        setError("Application failed. Please try again.");
                    }
                }, remaining);
            })
            .catch(err => {
                console.error("Apply error:", err);
                setPhase(PHASES.ERROR);
                setError("Application failed. Please try again.");
            });
    };

    const positionName = job?.position?.name || "this role";
    const yachtName = (() => {
        const y = job?.yacht;
        if (!y) return null;
        if (y.size && y.yachtType) return `${y.size}m ${y.yachtType}`;
        return y.name || null;
    })();

    const handleBackdropClick = (e) => {
        if (phase === PHASES.SENDING) return;
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <Overlay>
            <div className="apply-modal-positioner" onClick={handleBackdropClick}>
                <div className={`apply-modal apply-modal--${phase}`} role="dialog" aria-modal="true" aria-labelledby="apply-modal-title">

                    {phase === PHASES.CONFIRM && (
                        <>
                            <p className="apply-modal-eyebrow">Apply for this role</p>
                            <h2 className="apply-modal-title" id="apply-modal-title">{positionName}</h2>
                            {yachtName && <p className="apply-modal-subtitle">{yachtName}</p>}
                            <p className="apply-modal-body">
                                Your profile and CV will be shared with J4Crew. They'll review it and get in touch if you look like a fit.
                            </p>
                            <div className="apply-modal-actions">
                                <button type="button" className="apply-modal-button apply-modal-button--ghost" onClick={onClose}>Cancel</button>
                                <button type="button" className="apply-modal-button apply-modal-button--primary" onClick={handleSend}>Send my application</button>
                            </div>
                        </>
                    )}

                    {phase === PHASES.SENDING && (
                        <div className="apply-modal-state apply-modal-state--sending">
                            <div className="apply-modal-spinner" aria-hidden="true" />
                            <h2 className="apply-modal-title" id="apply-modal-title">Sending your application…</h2>
                            <p className="apply-modal-body">Sharing your profile with J4Crew.</p>
                        </div>
                    )}

                    {phase === PHASES.SUCCESS && (
                        <div className="apply-modal-state apply-modal-state--success">
                            <div className="apply-modal-celebrate" aria-hidden="true">
                                <span className="apply-modal-burst" />
                                <span className="apply-modal-check">
                                    <svg viewBox="0 0 32 32" width="32" height="32" fill="none">
                                        <path d="M7 17 L13 23 L25 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                            </div>
                            <h2 className="apply-modal-title" id="apply-modal-title">Application sent</h2>
                            <p className="apply-modal-body">
                                J4Crew has your profile for <strong>{positionName}</strong>{yachtName ? <> on a <strong>{yachtName}</strong></> : null}. You'll hear back via phone or email if there's a fit. Good luck.
                            </p>
                            <p className="apply-modal-hint">
                                You can still edit your profile any time before they get in touch.
                            </p>
                            <div className="apply-modal-actions">
                                <button type="button" className="apply-modal-button apply-modal-button--ghost" onClick={goToCv}>See your CV</button>
                                <button type="button" className="apply-modal-button apply-modal-button--primary" onClick={onClose}>Done</button>
                            </div>
                        </div>
                    )}

                    {phase === PHASES.ERROR && (
                        <div className="apply-modal-state apply-modal-state--error">
                            <h2 className="apply-modal-title" id="apply-modal-title">Couldn't send your application</h2>
                            <p className="apply-modal-body">{error || "Please try again in a moment."}</p>
                            <div className="apply-modal-actions">
                                <button type="button" className="apply-modal-button apply-modal-button--ghost" onClick={onClose}>Close</button>
                                <button type="button" className="apply-modal-button apply-modal-button--primary" onClick={handleSend}>Try again</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Overlay>
    );
};
